/* eslint-disable @typescript-eslint/no-unused-vars */
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import {
    ICompanyAutomationsRepsonse,
    IAutomationInstance
} from '~/types/automation.types';
import {
    v2DeleteWorkflowPath,
    v2DuplicateWorkflowPath,
    v2GetWorkflowsForCompanyPath
} from '~/utils/ts-routes.utils';
import { IWorkflowAutomationInfo } from '~/types/server.types';

export type UpdateAutomationRequest = Pick<
    IWorkflowAutomationInfo,
    | 'is_automation_on'
    | 'relevant_time_frame'
    | 'questionnaire_template_id'
    | 'flow_template_id'
    | 'email_template_id'
    | 'html_body'
    | 'subject'
> & { automation_type_cd: number };

export class AutomationsAdapter extends BaseHttpAdapter {
    async fetchAutomationPreviews(currCompanyId: string) {
        const data =
            await this.apiService.apiRequest<ICompanyAutomationsRepsonse>({
                method: 'get',
                url: v2GetWorkflowsForCompanyPath(currCompanyId)
            });

        return data;
    }

    async deleteAutomationFromList({
        automationPreviewId,
        currCompanyId
    }: {
        automationPreviewId: string;
        currCompanyId: string;
    }) {
        const data =
            await this.apiService.apiRequest<ICompanyAutomationsRepsonse>({
                method: 'delete',
                url: v2DeleteWorkflowPath(currCompanyId, automationPreviewId)
            });

        return data;
    }

    async duplicateAutomation({
        automationPreviewId,
        newName,
        currCompanyId
    }: {
        automationPreviewId: string;
        currCompanyId: string;
        newName: string;
    }) {
        const data =
            await this.apiService.apiRequest<ICompanyAutomationsRepsonse>({
                method: 'post',
                url: v2DuplicateWorkflowPath(
                    currCompanyId,
                    automationPreviewId
                ),
                data: {
                    save_as: newName
                }
            });

        return data;
    }

    async createNewAutomation(currCompanyId: string) {
        const { workflows: automationsPreviews } =
            await this.apiService.apiRequest<ICompanyAutomationsRepsonse>({
                method: 'post',
                url: `/api/v2/company/${currCompanyId}/workflows`
            });

        return automationsPreviews;
    }

    async updateAutomationProjectTypes({
        automationPreviewId,
        currCompanyId,
        values
    }: {
        automationPreviewId: string;
        currCompanyId: string;
        values: string[];
    }) {
        const {
            workflows: automationsPreviews,
            lead_forms_workflow_view_models: leadFormRelations
        } = await this.apiService.apiRequest<ICompanyAutomationsRepsonse>({
            method: 'put',
            url: `/api/v2/company/${currCompanyId}/workflows/${automationPreviewId}/project_type`,
            data: {
                relevant_project_types: values
            }
        });

        return { automationsPreviews, leadFormRelations };
    }

    async updateAutomationLeadForms({
        automationPreviewId,
        currCompanyId,
        value,
        leadFormId
    }: {
        automationPreviewId: string;
        currCompanyId: string;
        value: boolean;
        leadFormId: string;
    }) {
        const {
            workflows: automationsPreviews,
            lead_forms_workflow_view_models: leadFormRelations
        } = await this.apiService.apiRequest<ICompanyAutomationsRepsonse>({
            method: 'put',
            url: `/api/v2/company/${currCompanyId}/workflows/${automationPreviewId}/public_files`,
            data: {
                public_flow_ids_for_update: {
                    [leadFormId]: value
                },
                workflow_id: automationPreviewId
            }
        });

        return { automationsPreviews, leadFormRelations };
    }

    async updateAutomation(
        companyId: string,
        automation: UpdateAutomationRequest
    ) {
        await this.apiService.apiRequest<unknown>({
            method: 'put',
            url: `/api/v2/companies/${companyId}/automation`,
            data: automation
        });
    }

    async fetchAutomationInstance(workspaceId: string) {
        const { workflow: automation } = await this.apiService.apiRequest<{
            _id: string;
            workflow: IAutomationInstance;
        }>({
            method: 'get',
            url: `/api/v2/workspaces/${workspaceId}/workflow`,
            networkLabel: 'fetchAutomationInstance'
        });

        return automation;
    }

    async deleteAutomationInstance(workspaceId: string, automationId: string) {
        const response = await this.apiService.apiRequest<IAutomationInstance>({
            method: 'delete',
            url: `/api/v2/workspaces/${workspaceId}/workflows/${automationId}`
        });

        return response;
    }
}
